import React from "react";
import { Helmet } from "react-helmet";
import { NavLink } from "react-router-dom";

export default function PrivacyPolicy() {
  const curDate = new Date();

  const meta = {
    title: "Privacy Policy | EliteUKCasino",
    description:
      "Review the Privacy Policy for EliteUKCasino.com. Learn how we collect, use, store, and protect your personal data.",
  };

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>

      <div className="_w-100 text-bg-dark text-white pt-5 pb-5 mt-3 information">
        <div className="m-auto pt-3">
          <span>
            Last Updated: {curDate.toLocaleString("default", { month: "long" })}{" "}
            {curDate.getFullYear()}
          </span>

          {/* INTRODUCTION */}
          <div className="mt-4">
            <p>
              This Privacy Policy explains how eliteukcasino.com ("Website",
              "we", "us", or "our") collects, uses, discloses, and protects your
              personal information.
            </p>
          </div>

          {/* DATA CONTROLLER */}
          <div className="mt-4">
            <h4>Data Controller</h4>
            <p>
              EliteUKcasino.com is operated by <strong>BPCT, S.A.</strong>,
              registered in Panama
            </p>

            <p>
              For the purposes of applicable data protection laws, including the
              UK GDPR and EU GDPR where applicable, the above entity is the Data
              Controller of your personal data.
            </p>
            <p>
              You may contact us at:{" "}
              <a href="mailto:info@bpct.net">info@bpct.net</a>
            </p>
          </div>

          {/* INFORMATION WE COLLECT */}
          <div className="mt-4">
            <h4>Information We Collect</h4>

            <h6>Information You Provide</h6>
            <p>
              We may collect personal information that you voluntarily provide,
              including your name, email address, phone number, or other details
              submitted through contact forms, newsletters, or promotional
              entries.
            </p>

            <h6>Information Collected Automatically</h6>
            <ul>
              <li>IP address</li>
              <li>Device type and browser information</li>
              <li>Operating system and language</li>
              <li>Pages visited and time spent on the Website</li>
              <li>Referral URLs</li>
              <li>Cookie identifiers</li>
            </ul>
          </div>

          {/* PURPOSE OF PROCESSING */}
          <div className="mt-4">
            <h4>How We Use Your Information</h4>
            <ul>
              <li>To operate and maintain our Website</li>
              <li>To respond to inquiries and customer support requests</li>
              <li>To send newsletters (where consent is provided)</li>
              <li>To analyze traffic and improve user experience</li>
              <li>To prevent fraud and maintain security</li>
              <li>To comply with legal obligations</li>
              <li>To display relevant advertising and affiliate offers</li>
            </ul>
          </div>

          {/* LEGAL BASIS */}
          <div className="mt-4">
            <h4>Legal Basis for Processing</h4>
            <ul>
              <li>Consent</li>
              <li>Performance of a contract</li>
              <li>Legitimate interests</li>
              <li>Legal obligations</li>
            </ul>
          </div>

          {/* COOKIES */}
          <div className="mt-4">
            <h4>Cookies and Tracking Technologies</h4>
            <p>
              We use cookies and similar technologies to enhance user
              experience, analyze traffic, and provide personalized advertising.
            </p>
            <p>
              For more details, please review our{" "}
              <NavLink to={`/cookie-consent-policy/${window.location.search}`}>
                Cookie Policy
              </NavLink>
              .
            </p>
          </div>

          {/* THIRD PARTY SHARING */}
          <div className="mt-4">
            <h4>Sharing of Information</h4>
            <p>We may share your data with:</p>
            <ul>
              <li>Service providers (hosting, analytics, email providers)</li>
              <li>Advertising and affiliate partners</li>
              <li>Legal authorities where required by law</li>
            </ul>
            <p>
              When you click on third-party casino offers, you may be redirected
              to a partner website. Their privacy policy applies to any data you
              submit on their platform.
            </p>
          </div>

          {/* DATA RETENTION */}
          <div className="mt-4">
            <h4>Data Retention</h4>
            <p>
              We retain personal data only as long as necessary for the purposes
              outlined in this policy or as required by law.
            </p>
          </div>

          {/* INTERNATIONAL TRANSFERS */}
          <div className="mt-4">
            <h4>International Data Transfers</h4>
            <p>
              Your information may be processed outside the UK or EEA. Where
              this occurs, we ensure appropriate safeguards are in place in
              accordance with applicable data protection laws.
            </p>
          </div>

          {/* USER RIGHTS */}
          <div className="mt-4">
            <h4>Your Data Protection Rights</h4>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing</li>
              <li>Withdraw consent at any time</li>
              <li>
                Lodge a complaint with the UK Information Commissioner’s Office
                (ICO)
              </li>
            </ul>
          </div>

          {/* MARKETING OPT OUT */}
          <div className="mt-4">
            <h4>Marketing Communications Opt-Out</h4>
            <p>You may opt out of receiving marketing emails at any time by:</p>
            <ul>
              <li>Clicking the “unsubscribe” link in our emails</li>
              <li>
                Contacting us at{" "}
                <a href="mailto:info@bpct.net">info@bpct.net</a>
              </li>
            </ul>
          </div>

          {/* SECURITY */}
          <div className="mt-4">
            <h4>Security</h4>
            <p>
              We implement appropriate technical and organizational measures to
              protect personal data against unauthorized access, alteration,
              disclosure, or destruction.
            </p>
          </div>

          {/* POLICY UPDATES */}
          <div className="mt-4">
            <h4>Changes to This Policy</h4>
            <p>
              We may update this Privacy Policy from time to time. Updates will
              be posted on this page with a revised “Last Updated” date.
            </p>
          </div>

          {/* CONTACT */}
          <div className="mt-4">
            <h4>Contact Us</h4>
            <p>
              If you have any questions regarding this Privacy Policy, please
              contact us at:
            </p>
            <a href="mailto:info@bpct.net">info@bpct.net</a>
          </div>
        </div>
      </div>
    </>
  );
}
